import {Lock, MessageSquare, Phone, Users, Video} from 'lucide-react';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {PhoneHQAvatar} from 'ch3-ui-lib';
import type {Call} from '../../store/features/calls/callsService';
import type {OpenChatMessageLocationState} from '../../hooks/useOpenDirectChat';
import {matrixService} from '../../services/matrixService';
import {groupCallMessages as m} from '../../features/groupCalls/messages';
import {openRoomCallLobby} from '../../features/groupCalls/components/groupCallActions';
import {
    groupCallOutcomeMessage,
    groupCallParticipantName,
    groupCallParticipantStatus,
    groupCallParticipantStatusMessage,
    isGroupCallOutcomeMissed,
} from '../../features/groupCalls/services/groupCallCallPresentation';
import {useGroupCallRoomName} from '../../features/groupCalls/hooks/useGroupCallRoomName';
import {groupHistoryMessages as hm} from './groupHistoryMessages';
import {chip, formatCallDate, InfoRow, lucide} from './callParts';
import {ReportCallProblem} from './ReportCallProblem';

interface GroupCallDetailsProps {
    selectedCall: Call;
}

const STATUS_STYLES = {
    started: 'text-brand-ink',
    joined: 'text-success-ink',
    declined: 'text-danger-ink',
    missed: 'text-danger-ink',
} as const;

const SECOND_MS = 1_000;

const lengthOf = (call: Call): number | undefined => {
    const from = Date.parse(call.bridgetAt || call.startedAt);
    const to = Date.parse(call.finishedAt);
    return call.bridgetAt && Number.isFinite(from) && Number.isFinite(to) && to >= from ? to - from : undefined;
};

/** Group call details: same layout as a 1:1 call, filling the details area. */
const GroupCallDetails = ({selectedCall}: GroupCallDetailsProps) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const roomName = useGroupCallRoomName(selectedCall.roomId);
    const missed = isGroupCallOutcomeMissed(selectedCall);
    const roomId = selectedCall.roomId;
    const intent = selectedCall.callIntent === 'video' ? 'video' : 'audio';
    const lengthMs = lengthOf(selectedCall);
    const members = roomId ? matrixService.getRoom(roomId)?.getJoinedMemberCount() : undefined;
    const participants = selectedCall.participants ?? [];

    const openRoom = () => {
        if (!roomId) return;
        const state: OpenChatMessageLocationState = {openChatMessage: {roomId}};
        navigate('/chat', {state});
    };

    return (
        <div className="flex flex-col gap-4 px-8 py-6" data-testid="group-call-details">
            {/* Header: the room, encryption, and what you can do */}
            <div className="flex items-center gap-4">
                <span className={`flex size-12 shrink-0 items-center justify-center rounded-xl ${missed ? 'bg-danger-tint text-danger-ink' : 'bg-brand-tint text-brand-ink'}`}>
                    <Users size={22} aria-hidden="true" className={lucide}/>
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <h1 className="m-0 truncate text-headline text-ink">{roomName}</h1>
                    <div>
                        <span className={chip}>
                            <Lock size={14} aria-hidden="true" className={lucide}/>
                            {intl.formatMessage(hm.internalEncrypted)}
                        </span>
                    </div>
                </div>
                {roomId && (
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            data-testid="group-call-details-start"
                            onClick={() => openRoomCallLobby(roomId, intent, 'calls')}
                            className="inline-flex h-control-md cursor-pointer items-center gap-2 rounded-control bg-brand px-4 text-callout font-medium text-white hover:bg-brand-hover"
                        >
                            <Phone size={16} aria-hidden="true" className={lucide}/>
                            {intl.formatMessage(hm.startCall)}
                        </button>
                        <button
                            type="button"
                            data-testid="group-call-details-open-chat"
                            onClick={openRoom}
                            className="inline-flex h-control-md cursor-pointer items-center gap-2 rounded-control bg-surface px-4 text-callout font-medium text-ink hover:bg-line"
                        >
                            <MessageSquare size={16} aria-hidden="true" className={lucide}/>
                            {intl.formatMessage(hm.openChat)}
                        </button>
                    </div>
                )}
            </div>

            {/* Facts about the call */}
            <section aria-label={intl.formatMessage(hm.detailsRegion)} className="rounded-card bg-surface-muted px-1 pb-1">
                <div className="flex h-10 items-center gap-2 px-3 text-callout font-semibold text-ink">
                    {intent === 'video' ? <Video size={16} aria-hidden="true" className={lucide}/> : <Phone size={16} aria-hidden="true" className={lucide}/>}
                    {intl.formatMessage(hm.typeValue, {intent: selectedCall.callIntent ?? 'unknown'})}
                </div>
                <div className="rounded-control bg-surface">
                    <InfoRow label={intl.formatMessage(hm.date)}>
                        {selectedCall.startedAt ? formatCallDate(selectedCall.startedAt) : '—'}
                    </InfoRow>
                    <InfoRow label={intl.formatMessage(hm.length)}>
                        {lengthMs === undefined
                            ? '—'
                            : intl.formatMessage(hm.lengthValue, {minutes: Math.floor(lengthMs / 60_000), seconds: Math.floor((lengthMs % 60_000) / SECOND_MS)})}
                    </InfoRow>
                    <InfoRow label={intl.formatMessage(hm.result)}>
                        <span className={missed ? 'text-danger-ink' : ''}>{intl.formatMessage(groupCallOutcomeMessage(selectedCall))}</span>
                    </InfoRow>
                    <InfoRow label={intl.formatMessage(hm.chat)}>
                        <span className="break-words">
                            {members ? intl.formatMessage(hm.chatValue, {room: roomName, count: members}) : roomName}
                        </span>
                    </InfoRow>
                </div>
            </section>

            {/* Who was in it */}
            <section aria-label={intl.formatMessage(m.callsDetailsParticipants)} className="rounded-card bg-surface-muted px-1 pb-1">
                <p className="m-0 flex h-10 items-center px-3 text-callout font-semibold text-ink">{intl.formatMessage(m.callsDetailsParticipants)}</p>
                <ul className="m-0 list-none rounded-control bg-surface px-4 py-1" data-testid="group-call-details-participants">
                    {participants.map(participant => {
                        const status = groupCallParticipantStatus(selectedCall, participant);
                        const name = groupCallParticipantName(participant) ?? intl.formatMessage(m.historyUnknownUser);
                        return (
                            <li key={participant.userId} className="flex min-h-11 items-center justify-between gap-3 border-0 border-t border-solid border-surface-muted py-2 first:border-t-0">
                                <div className="flex min-w-0 items-center gap-3">
                                    <PhoneHQAvatar name={name} src={participant.avatarUrl} size="32px"/>
                                    <span className="truncate text-callout text-ink">{name}</span>
                                </div>
                                <span className={`shrink-0 text-footnote font-medium ${STATUS_STYLES[status]}`}>
                                    {intl.formatMessage(groupCallParticipantStatusMessage(status))}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </section>

            <ReportCallProblem callData={{
                callType: 'GROUP',
                callId: selectedCall.callId,
                roomId,
                callIntent: selectedCall.callIntent,
                participants: participants.length,
                callLength: lengthMs === undefined ? undefined : Math.round(lengthMs / SECOND_MS) + 's',
                callStart: selectedCall.startedAt,
                platform: selectedCall.platform,
            }}/>
        </div>
    );
};

export default GroupCallDetails;
